import { designVariables } from "@studio3t/design-variables";
import {
  BadgeSecondarySubtle,
  ButtonSize,
  GenericButton,
  Icon,
  IconSVG,
  SVGColorKey,
} from "@studio3t/generic-components";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import { OverlayScroll } from "@/components/OverlayScroll";
import { ServiceStatusBadge } from "@/components/ServiceStatusBadge";
import { SkeletonBar, SkeletonPill } from "@/components/Skeleton";
import { useJobs, useServices } from "@/hooks/useApi";
import { usePersistedState } from "@/hooks/usePersistedState";
import type { Service } from "@/types/api";
import { parseAllowedList } from "@/utils/storedValue";
import {
  CardFooter,
  CardHeader,
  CardTitle,
  DOCS_URL,
  FooterLink,
  GUTTER,
} from "./dashboardCards";
import {
  getProviderIcon,
  PROVIDER_LABELS,
  providerRank,
  SERVICE_PROVIDERS,
  type ServiceStatusValue,
  statusFor,
} from "./Services/helpers";
import { useServiceData } from "./Services/useServiceData";

const SINGLE_LINE = designVariables.type.singleLine;
const LIST = designVariables.ui.list;

const ScrollArea = styled(OverlayScroll)`
  flex: 1;
  /* Size containment keeps the list out of the card's intrinsic height, so
     the card's floor is its min-height and long lists scroll instead of
     growing the dashboard. */
  contain: size;
  min-height: 0;
`;

const TypeRow = styled.button`
  display: flex;
  align-items: center;
  gap: ${LIST.item.gap}px;
  box-sizing: border-box;
  width: 100%;
  height: ${LIST.item.height}px;
  padding: 0 ${GUTTER}px;
  border: none;
  background: none;
  font: inherit;
  text-align: inherit;
  cursor: pointer;

  &:hover {
    background: ${(props) => props.theme.tableTbodyCellHoverBackground};
  }
`;

const Chevron = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${LIST.item.wrap}px;
  flex-shrink: 0;
  transition: transform 0.15s ease;
  transform: rotate(${(props) => (props.$expanded ? 90 : 0)}deg);
`;

const TypeLabel = styled.span`
  flex: 1;
  min-width: 0;
  font-size: ${SINGLE_LINE.default.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.default.medium.fontWeight};
  color: ${(props) => props.theme.semanticTextBody};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
`;

// Child rows sit at the design's second indent level (list/item/indent2).
const ServiceRow = styled.button`
  display: flex;
  align-items: center;
  gap: ${LIST.item.gap}px;
  box-sizing: border-box;
  width: 100%;
  height: ${LIST.item.height}px;
  padding: 0 ${LIST.item.paddingw}px 0 ${LIST.item.indent2}px;
  border: none;
  background: none;
  font: inherit;
  text-align: inherit;
  cursor: pointer;

  &:hover {
    background: ${(props) => props.theme.tableTbodyCellHoverBackground};
  }
`;

const ServiceIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${LIST.item.wrap}px;
  height: ${LIST.item.wrap}px;
  flex-shrink: 0;
`;

// Unlike the type label, the name hugs its content so the status badge sits
// right beside it (Figma 14879-242146).
const ServiceName = styled.span`
  min-width: 0;
  font-size: ${SINGLE_LINE.default.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.default.medium.fontWeight};
  color: ${(props) => props.theme.semanticTextBody};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StateMessage = styled.div`
  padding: ${GUTTER}px 0;
  text-align: center;
  font-size: ${SINGLE_LINE.small.regular.fontSize}px;
  font-style: italic;
  color: ${(props) => props.theme.semanticTextLight};
`;

const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${LIST.item.gap}px;
  height: ${LIST.item.height}px;
  padding: 0 ${GUTTER}px;
`;

const SKELETON_ROWS = Array.from({ length: 5 }, (_, i) => `skeleton-${i}`);

function SkeletonList() {
  return (
    <>
      {SKELETON_ROWS.map((key) => (
        <SkeletonRow key={key} aria-hidden="true">
          <SkeletonBar $width="40%" />
          <SkeletonPill $width="20px" />
        </SkeletonRow>
      ))}
    </>
  );
}

interface ServiceGroup {
  provider: Service["provider"];
  label: string;
  services: { service: Service; status: ServiceStatusValue }[];
}

// One provider group: the row that expands it, plus its service rows while
// expanded.
function ServiceGroupSection({
  group,
  expanded,
  onToggle,
  onSelectService,
}: {
  group: ServiceGroup;
  expanded: boolean;
  onToggle: () => void;
  onSelectService: (service: Service) => void;
}) {
  return (
    <div>
      <TypeRow type="button" aria-expanded={expanded} onClick={onToggle}>
        <Chevron $expanded={expanded}>
          <IconSVG
            icon={Icon.Chevron}
            size={LIST.item.chevron}
            svgColorKey={SVGColorKey.semanticTextBody}
          />
        </Chevron>
        <TypeLabel>{group.label}</TypeLabel>
        <BadgeSecondarySubtle>{group.services.length}</BadgeSecondarySubtle>
      </TypeRow>
      {expanded &&
        group.services.map(({ service, status }) => (
          <ServiceRow
            key={service.name}
            type="button"
            onClick={() => onSelectService(service)}
          >
            <ServiceIconWrap>
              <IconSVG
                icon={getProviderIcon(service.provider)}
                size={LIST.item.icon}
                svgColorKey={SVGColorKey.semanticTextBody}
              />
            </ServiceIconWrap>
            <ServiceName>{service.name}</ServiceName>
            {status !== "unknown" && (
              <ServiceStatusBadge live={status === "live"} />
            )}
          </ServiceRow>
        ))}
    </div>
  );
}

// The card body: skeletons while the first load is in flight, then the grouped
// list — or a message when there is nothing to show.
function ServicesList({
  loading,
  error,
  groups,
  expanded,
  onToggle,
  onSelectService,
}: {
  loading: boolean;
  error: Error | null;
  groups: ServiceGroup[];
  expanded: Set<Service["provider"]>;
  onToggle: (provider: Service["provider"]) => void;
  onSelectService: (service: Service) => void;
}) {
  return (
    <>
      {loading && <SkeletonList />}
      {error && <StateMessage>Failed to load services</StateMessage>}
      {!loading && !error && groups.length === 0 && (
        <StateMessage>You have not added any services yet</StateMessage>
      )}
      {!loading &&
        !error &&
        groups.map((group) => (
          <ServiceGroupSection
            key={group.provider}
            group={group}
            expanded={expanded.has(group.provider)}
            onToggle={() => onToggle(group.provider)}
            onSelectService={onSelectService}
          />
        ))}
    </>
  );
}

function ServicesHeader({ services }: { services: Service[] | undefined }) {
  const theme = useTheme();

  return (
    <CardHeader>
      <IconSVG
        icon={Icon.Service}
        size={20}
        colors={{ default: theme.semanticTextHeading }}
      />
      <CardTitle>Services</CardTitle>
      {services && (
        <BadgeSecondarySubtle>{services.length}</BadgeSecondarySubtle>
      )}
    </CardHeader>
  );
}

// Groups the services by provider, ordering the groups the way the Services
// page does and the rows inside each one by name.
function groupByProvider(
  services: Service[] | undefined,
  getServiceUsage: ReturnType<typeof useServiceData>["getServiceUsage"],
  usageReady: boolean,
): ServiceGroup[] {
  const byProvider = new Map<Service["provider"], ServiceGroup>();
  for (const service of services ?? []) {
    let group = byProvider.get(service.provider);
    if (!group) {
      group = {
        provider: service.provider,
        label: PROVIDER_LABELS[service.provider] ?? service.provider,
        services: [],
      };
      byProvider.set(service.provider, group);
    }
    group.services.push({
      service,
      status: statusFor(service, getServiceUsage(service), usageReady),
    });
  }
  for (const group of byProvider.values()) {
    group.services.sort((a, b) => a.service.name.localeCompare(b.service.name));
  }
  return [...byProvider.values()].sort(
    (a, b) => providerRank(a.provider) - providerRank(b.provider),
  );
}

// Which provider groups the user opened, remembered per user (TTTL-186);
// all collapsed is the default and is stored as absence.
const EXPANDED_STORAGE_KEY = "dashboard:services-expanded";

function serializeExpanded(expanded: Set<Service["provider"]>): string | null {
  return expanded.size ? JSON.stringify([...expanded]) : null;
}

function useExpandedProviders() {
  return usePersistedState<Set<Service["provider"]>>(
    EXPANDED_STORAGE_KEY,
    (raw) => new Set(parseAllowedList(raw, SERVICE_PROVIDERS)),
    serializeExpanded,
  );
}

export function DashboardServices() {
  const navigate = useNavigate();
  const { data: services, isLoading, error } = useServices();
  const { data: jobs, error: jobsError } = useJobs();
  const { getServiceUsage } = useServiceData(jobs ?? null);
  const [expanded, setExpanded] = useExpandedProviders();

  const usageReady = Boolean(jobs) && !jobsError;

  const groups = useMemo(
    () => groupByProvider(services, getServiceUsage, usageReady),
    [services, getServiceUsage, usageReady],
  );

  const toggle = (provider: Service["provider"]) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(provider)) next.delete(provider);
      else next.add(provider);
      return next;
    });
  };

  const openService = (service: Service) => {
    navigate(`/services/${encodeURIComponent(service.name)}`);
  };

  const loading = isLoading && !services;

  return (
    <>
      <ServicesHeader services={services} />

      <ScrollArea>
        <ServicesList
          loading={loading}
          error={error}
          groups={groups}
          expanded={expanded}
          onToggle={toggle}
          onSelectService={openService}
        />
      </ScrollArea>

      <CardFooter>
        <FooterLink
          size="L"
          href={DOCS_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Read more about services
        </FooterLink>
        <GenericButton
          text="Add service"
          type="secondary"
          size={ButtonSize.Small}
          icon={Icon.Plus}
          iconPosition="leading"
          onClick={() => navigate("/services/new")}
        />
      </CardFooter>
    </>
  );
}
